/**
 * Byte helpers - random, encode, decode, concat, equal.
 *
 * All helpers return plain Uint8Array; brand them at the call site with the
 * `asBrand<T>(...)` helper from @shared/types when you need a typed brand.
 *
 * Uses Web Crypto + standard text encoders, available identically in browser,
 * Node 20+, and Cloudflare Workers.
 */

/** Crypto-secure random bytes. */
export function randomBytes(length: number): Uint8Array {
  if (!Number.isInteger(length) || length < 0) {
    throw new RangeError(`randomBytes: invalid length ${length}`);
  }
  const out = new Uint8Array(length);
  crypto.getRandomValues(out);
  return out;
}

/** Constant-time comparison. Returns false on length mismatch. */
export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let acc = 0;
  for (let i = 0; i < a.length; i++) {
    acc |= (a[i] ?? 0) ^ (b[i] ?? 0);
  }
  return acc === 0;
}

/** Concatenate any number of byte arrays into a single Uint8Array. */
export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const a of arrays) total += a.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

// =============================================================================
// Base64 (url-safe, no padding)
// =============================================================================

const URL_SAFE_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

export function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  const std = btoa(bin);
  return std.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromBase64Url(s: string): Uint8Array {
  // Validate alphabet to refuse silent corruption.
  for (const ch of s) {
    if (!URL_SAFE_ALPHABET.includes(ch)) {
      throw new SyntaxError(`fromBase64Url: invalid char ${JSON.stringify(ch)}`);
    }
  }
  const padLen = (4 - (s.length % 4)) % 4;
  const std = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(padLen);
  const bin = atob(std);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// =============================================================================
// Base64 (standard, with padding)
// =============================================================================

export function toBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

export function fromBase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// =============================================================================
// Hex
// =============================================================================

export function toHex(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) {
    s += (bytes[i]! < 16 ? "0" : "") + bytes[i]!.toString(16);
  }
  return s;
}

export function fromHex(s: string): Uint8Array {
  if (s.length % 2 !== 0) throw new SyntaxError("fromHex: odd length");
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) {
    const b = Number.parseInt(s.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(b)) throw new SyntaxError(`fromHex: invalid at ${i * 2}`);
    out[i] = b;
  }
  return out;
}

// =============================================================================
// Text
// =============================================================================

const TE = new TextEncoder();
const TD = new TextDecoder("utf-8", { fatal: true });

export function utf8Encode(s: string): Uint8Array {
  return TE.encode(s);
}

export function utf8Decode(bytes: Uint8Array): string {
  return TD.decode(bytes);
}

// =============================================================================
// SHA-256
// =============================================================================

export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    bytes as unknown as ArrayBuffer,
  );
  return new Uint8Array(buf);
}

// =============================================================================
// ULID (Crockford base32, 26 chars, sortable)
// Spec: https://github.com/ulid/spec
// Format: 10 chars time (48 bits) + 16 chars random (80 bits)
// =============================================================================

const ULID_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Monotonic state for ULID generation.
 *
 * Per the ULID spec, within a single millisecond the randomness MUST
 * monotonically increase: ulid(T)[k] < ulid(T)[k+1] when both are minted
 * at the same T. We implement this by remembering the last randomness
 * payload and, on a tied timestamp, incrementing it as a 80-bit big-endian
 * integer. On any new timestamp the state is reset.
 *
 * If the per-millisecond random space (80 bits) overflows back to zero
 * (vanishingly improbable), we deliberately re-randomize: the resulting
 * ULID may sort BEFORE its predecessor for that ms, but only after 2^80
 * mints in the same ms - not a realistic regime.
 */
const _ulidLastRand = new Uint8Array(10);
let _ulidLastNow = -1;

function _bumpRandom(buf: Uint8Array): void {
  for (let i = buf.length - 1; i >= 0; i--) {
    if (buf[i]! < 0xff) {
      buf[i] = buf[i]! + 1;
      return;
    }
    buf[i] = 0;
  }
  // Overflow: re-randomize.
  crypto.getRandomValues(buf);
}

/**
 * Generate a ULID at the given timestamp (ms). Same-ms ULIDs are guaranteed
 * to sort lexicographically in mint order (monotonicity).
 *
 * For deterministic tests, pass a fixed `now`. In production callers pass
 * Date.now().
 */
export function ulid(now: number): string {
  if (!Number.isInteger(now) || now < 0 || now > 0xffff_ffff_ffff) {
    throw new RangeError("ulid: invalid timestamp");
  }
  // 10 chars time
  let timeStr = "";
  let t = now;
  for (let i = 9; i >= 0; i--) {
    timeStr = ULID_ALPHABET.charAt(t % 32) + timeStr;
    t = Math.floor(t / 32);
  }

  // 80-bit randomness with same-ms monotonicity.
  let rand: Uint8Array;
  if (now === _ulidLastNow) {
    _bumpRandom(_ulidLastRand);
    rand = _ulidLastRand;
  } else {
    rand = randomBytes(10);
    _ulidLastRand.set(rand);
    _ulidLastNow = now;
  }

  let randStr = "";
  // Encode 10 bytes -> 16 base32 chars (5 bits per char).
  let bits = 0;
  let value = 0;
  for (let i = 0; i < rand.length; i++) {
    value = (value << 8) | rand[i]!;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      randStr += ULID_ALPHABET.charAt((value >>> bits) & 0x1f);
    }
  }
  if (bits > 0) {
    randStr += ULID_ALPHABET.charAt((value << (5 - bits)) & 0x1f);
  }
  return timeStr + randStr;
}

// =============================================================================
// UUID v4
// =============================================================================

export function uuidV4(): string {
  // Prefer the built-in when available; fall back to manual construction.
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const b = randomBytes(16);
  b[6] = (b[6]! & 0x0f) | 0x40; // version 4
  b[8] = (b[8]! & 0x3f) | 0x80; // variant 10
  const h = toHex(b);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
